import { useTheme } from "@emotion/react";
import { format, formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";
import { MdWarningAmber } from "react-icons/md";

import { TicketListItemDto } from "@megaron/support-contracts";

import { SupportTicketStripeTile } from "./SupportTicketStripeTile";
import { toDisplayName } from "./displayName";
import { isSupportTicketAgedOpen } from "./isSupportTicketAgedOpen";

export { AssigneesCell } from "./SupportTicketListAssigneesCell";

const priorityLabels: Record<string, string> = {
  low: "Niski",
  medium: "Średni",
  high: "Wysoki",
  urgent: "Pilny",
};

const priorityColor = (
  priority: TicketListItemDto["priority"],
  colors: { success: string; warning: string; danger: string; primary: string; secondaryText: string },
) => {
  if (priority === "low") return colors.success;
  if (priority === "medium") return colors.warning;
  if (priority === "high") return colors.danger;
  if (priority === "urgent") return colors.primary;
  return colors.secondaryText;
};

function updatedAgo(d: Date | undefined): string {
  if (!d) return "–";
  return formatDistanceToNow(new Date(d), { addSuffix: true, locale: pl });
}

export function TicketStatusTile({ item }: { item: TicketListItemDto }) {
  return <SupportTicketStripeTile item={item} />;
}

export function TicketDetailsCell({ item }: { item: TicketListItemDto }) {
  const theme = useTheme();
  const isAgedOpen = isSupportTicketAgedOpen(item);

  const reporterDisplayName = toDisplayName(item.reporterName || item.reporterEmail.split("@")[0]);

  return (
    <div css={{ minWidth: 0, minHeight: 40 }}>
      <div
        css={{
          display: "flex",
          alignItems: "center",
          gap: "0.375rem",
          fontWeight: item.status !== "open" ? 400 : item.isRead ? 400 : 700,
          color: "inherit",
        }}
      >
        {isAgedOpen && <MdWarningAmber css={{ color: theme.colors.warning, flexShrink: 0 }} size={16} />}
        <span css={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.subject}</span>
      </div>
      <div
        css={{
          fontSize: "0.8125rem",
          color: theme.colors.secondaryText,
          marginTop: "0.125rem",
          textDecoration: "none",
        }}
      >
        {[
          reporterDisplayName,
          item.lastReplyAt ?? item.createdAt
            ? `Zaktualizowano ${updatedAgo(item.lastReplyAt ?? item.createdAt)}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </div>
    </div>
  );
}

export function PriorityCell({ item }: { item: TicketListItemDto }) {
  const theme = useTheme();
  const priority = item.priority;
  const label = priority ? (priorityLabels[priority] ?? priority) : "–";
  const barColor = priorityColor(priority, theme.colors);

  return (
    <div>
      <div css={{ fontSize: "0.875rem" }}>{label}</div>
      <div css={{ marginTop: "0.25rem", height: 4, borderRadius: 2, background: barColor, maxWidth: 80 }} />
    </div>
  );
}

export function SourceMailboxCell({ item }: { item: TicketListItemDto }) {
  return <div css={{ fontSize: "0.875rem" }}>{item.sourceMailbox ?? "–"}</div>;
}

const categoryLabels: Record<string, string> = {
  billing: "Rozliczenia",
  technical: "Techniczne",
  sales: "Sprzedaż",
  complaint: "Reklamacja",
  other: "Inne",
};

export function CategoryCell({ item }: { item: TicketListItemDto }) {
  const theme = useTheme();
  const label = item.category ? (categoryLabels[item.category] ?? item.category) : "–";
  return <div css={{ fontSize: "0.875rem", color: item.category ? "inherit" : theme.colors.secondaryText }}>{label}</div>;
}

export function CreatedAtCell({ item }: { item: TicketListItemDto }) {
  const theme = useTheme();
  return (
    <div css={{ fontSize: "0.875rem", color: theme.colors.secondaryText, whiteSpace: "nowrap" }}>
      {format(new Date(item.createdAt), "dd.MM.yyyy", { locale: pl })}
    </div>
  );
}
